/**
 * MyPodOps Studio V2 - Progressive S3 Uploader
 *
 * Handles:
 * - Chunked upload during recording
 * - Multipart upload for large files
 * - Offline resilience with IndexedDB fallback
 * - Upload progress tracking
 * - Retry logic for failed chunks
 */

import { v4 as uuidv4 } from 'uuid';
import { RecordingTrack } from './recorder';

const API_BASE = 'https://jtpw1iado5.execute-api.us-east-2.amazonaws.com/prod';
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for multipart upload
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms

export interface UploadProgress {
  trackId: string;
  bytesUploaded: number;
  totalBytes: number;
  percentage: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
}

export interface UploadSession {
  sessionId: string;
  userId: string;
  tracks: Map<string, TrackUploadState>;
  startTime: number;
  metadata: SessionMetadata;
}

export interface SessionMetadata {
  title: string;
  participants: string[];
  quality: string;
  duration?: number;
}

interface TrackUploadState {
  trackId: string;
  uploadId?: string; // S3 multipart upload ID
  s3Key: string;
  parts: UploadPart[];
  chunks: Blob[];
  totalSize: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
}

interface UploadPart {
  partNumber: number;
  etag?: string;
  size: number;
  uploaded: boolean;
}

/**
 * Get presigned URL for S3 upload
 */
async function getPresignedUrl(
  s3Key: string,
  contentType: string,
  token: string
): Promise<string> {
  const response = await fetch(`${API_BASE}/studio/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      key: s3Key,
      contentType,
      action: 'putObject'
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to get presigned URL: ${response.statusText}`);
  }

  const data = await response.json();
  return data.url;
}

/**
 * Initialize multipart upload
 */
async function initiateMultipartUpload(
  s3Key: string,
  contentType: string,
  token: string
): Promise<string> {
  const response = await fetch(`${API_BASE}/studio/multipart/init`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      key: s3Key,
      contentType
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to initiate multipart upload: ${response.statusText}`);
  }

  const data = await response.json();
  return data.uploadId;
}

/**
 * Get presigned URL for multipart upload part
 */
async function getMultipartPresignedUrl(
  s3Key: string,
  uploadId: string,
  partNumber: number,
  token: string
): Promise<string> {
  const response = await fetch(`${API_BASE}/studio/multipart/part-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      key: s3Key,
      uploadId,
      partNumber
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to get part URL: ${response.statusText}`);
  }

  const data = await response.json();
  return data.url;
}

/**
 * Complete multipart upload
 */
async function completeMultipartUpload(
  s3Key: string,
  uploadId: string,
  parts: { partNumber: number; etag: string }[],
  token: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/studio/multipart/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      key: s3Key,
      uploadId,
      parts
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to complete multipart upload: ${response.statusText}`);
  }
}

/**
 * Upload a single chunk with retry logic
 */
async function uploadChunkWithRetry(
  url: string,
  chunk: Blob,
  retries = MAX_RETRIES
): Promise<string | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: chunk,
        headers: {
          'Content-Type': chunk.type || 'application/octet-stream'
        }
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      // Get ETag for multipart completion
      const etag = response.headers.get('ETag');
      return etag;
    } catch (error) {
      console.warn(`[Uploader] Chunk upload attempt ${attempt + 1} failed:`, error);

      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (attempt + 1)));
      } else {
        throw error;
      }
    }
  }

  return null;
}

/**
 * StudioUploader - Manages uploads for a recording session
 */
export class StudioUploader {
  private session: UploadSession;
  private token: string;
  private onProgress?: (progress: UploadProgress[]) => void;
  private db: IDBDatabase | null = null;

  constructor(
    sessionId: string,
    userId: string,
    token: string,
    metadata: SessionMetadata,
    onProgress?: (progress: UploadProgress[]) => void
  ) {
    this.session = {
      sessionId,
      userId,
      tracks: new Map(),
      startTime: Date.now(),
      metadata
    };
    this.token = token;
    this.onProgress = onProgress;

    // Initialize IndexedDB for offline fallback
    this.initIndexedDB();
  }

  /**
   * Initialize IndexedDB for offline storage
   */
  private async initIndexedDB(): Promise<void> {
    return new Promise((resolve, _reject) => {
      const request = indexedDB.open('podops-studio-v2', 1);

      request.onerror = () => {
        console.warn('[Uploader] IndexedDB not available');
        resolve();
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[Uploader] IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store for pending chunks
        if (!db.objectStoreNames.contains('pendingChunks')) {
          const store = db.createObjectStore('pendingChunks', { keyPath: 'id' });
          store.createIndex('sessionId', 'sessionId', { unique: false });
          store.createIndex('trackId', 'trackId', { unique: false });
        }

        // Store for session metadata
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'sessionId' });
        }
      };
    });
  }

  /**
   * Save chunk to IndexedDB for offline fallback
   */
  private async saveChunkOffline(trackId: string, chunkIndex: number, chunk: Blob): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, _reject) => {
      const tx = this.db!.transaction('pendingChunks', 'readwrite');
      const store = tx.objectStore('pendingChunks');

      store.put({
        id: `${this.session.sessionId}-${trackId}-${chunkIndex}`,
        sessionId: this.session.sessionId,
        trackId,
        chunkIndex,
        data: chunk,
        timestamp: Date.now()
      });

      tx.oncomplete = () => resolve();
      tx.onerror = () => _reject(tx.error);
    });
  }

  /**
   * Register a track for upload
   */
  registerTrack(track: RecordingTrack): void {
    const s3Key = `studio/recordings/${this.session.userId}/${this.session.sessionId}/${track.id}.webm`;

    this.session.tracks.set(track.id, {
      trackId: track.id,
      s3Key,
      parts: [],
      chunks: [],
      totalSize: 0,
      status: 'pending'
    });

    console.log(`[Uploader] Registered track: ${track.id}`);
  }

  /**
   * Handle incoming chunk from recorder
   */
  async handleChunk(track: RecordingTrack, chunk: Blob): Promise<void> {
    const state = this.session.tracks.get(track.id);
    if (!state) {
      console.warn(`[Uploader] Unknown track: ${track.id}`);
      return;
    }

    // Add to local buffer
    state.chunks.push(chunk);
    state.totalSize += chunk.size;

    // Save to IndexedDB for offline fallback
    await this.saveChunkOffline(track.id, state.chunks.length - 1, chunk);

    console.log(`[Uploader] Chunk received: ${track.id}, size: ${(chunk.size / 1024).toFixed(2)} KB`);

    // Update progress
    this.reportProgress();
  }

  /**
   * Finalize and upload a completed track
   */
  async finalizeTrack(track: RecordingTrack, blob: Blob): Promise<string> {
    const state = this.session.tracks.get(track.id);
    if (!state) {
      throw new Error(`Unknown track: ${track.id}`);
    }

    state.status = 'uploading';
    this.reportProgress();

    try {
      // For large files (>100MB), use multipart upload
      if (blob.size > 100 * 1024 * 1024) {
        await this.uploadMultipart(state, blob);
      } else {
        await this.uploadDirect(state, blob);
      }

      state.status = 'completed';
      this.reportProgress();

      const s3Url = `https://www.mypodops.com/${state.s3Key}`;
      console.log(`[Uploader] Track uploaded: ${track.id} -> ${s3Url}`);

      return s3Url;
    } catch (error) {
      state.status = 'failed';
      this.reportProgress();
      throw error;
    }
  }

  /**
   * Direct upload for smaller files
   */
  private async uploadDirect(state: TrackUploadState, blob: Blob): Promise<void> {
    const url = await getPresignedUrl(state.s3Key, blob.type, this.token);
    await uploadChunkWithRetry(url, blob);
  }

  /**
   * Multipart upload for large files
   */
  private async uploadMultipart(state: TrackUploadState, blob: Blob): Promise<void> {
    // Initialize multipart upload
    state.uploadId = await initiateMultipartUpload(state.s3Key, blob.type, this.token);

    // Split into chunks
    const chunks: Blob[] = [];
    let offset = 0;
    while (offset < blob.size) {
      chunks.push(blob.slice(offset, offset + CHUNK_SIZE));
      offset += CHUNK_SIZE;
    }

    // Initialize parts tracking
    state.parts = chunks.map((chunk, i) => ({
      partNumber: i + 1,
      size: chunk.size,
      uploaded: false
    }));

    // Upload parts in parallel (max 4 concurrent)
    const uploadedParts: { partNumber: number; etag: string }[] = [];
    const concurrency = 4;

    for (let i = 0; i < chunks.length; i += concurrency) {
      const batch = chunks.slice(i, i + concurrency);
      const batchPromises = batch.map(async (chunk, j) => {
        const partNumber = i + j + 1;
        const url = await getMultipartPresignedUrl(
          state.s3Key,
          state.uploadId!,
          partNumber,
          this.token
        );

        const etag = await uploadChunkWithRetry(url, chunk);
        if (etag) {
          state.parts[partNumber - 1].uploaded = true;
          state.parts[partNumber - 1].etag = etag.replace(/"/g, '');
          uploadedParts.push({ partNumber, etag: etag.replace(/"/g, '') });
        }

        this.reportProgress();
      });

      await Promise.all(batchPromises);
    }

    // Complete multipart upload
    await completeMultipartUpload(
      state.s3Key,
      state.uploadId!,
      uploadedParts.sort((a, b) => a.partNumber - b.partNumber),
      this.token
    );
  }

  /**
   * Save session metadata to DynamoDB
   */
  async saveSession(): Promise<void> {
    const tracks = Array.from(this.session.tracks.values()).map(state => ({
      trackId: state.trackId,
      s3Key: state.s3Key,
      status: state.status,
      size: state.totalSize
    }));

    const response = await fetch(`${API_BASE}/studio/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        sessionId: this.session.sessionId,
        userId: this.session.userId,
        metadata: this.session.metadata,
        tracks,
        startTime: this.session.startTime,
        endTime: Date.now()
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to save session: ${response.statusText}`);
    }

    console.log(`[Uploader] Session saved: ${this.session.sessionId}`);
  }

  /**
   * Report progress to callback
   */
  private reportProgress(): void {
    if (!this.onProgress) return;

    const progress: UploadProgress[] = Array.from(this.session.tracks.values()).map(state => {
      const uploadedParts = state.parts.filter(p => p.uploaded).length;
      const totalParts = state.parts.length || 1;
      const uploadedSize = uploadedParts * CHUNK_SIZE;

      return {
        trackId: state.trackId,
        bytesUploaded: Math.min(uploadedSize, state.totalSize),
        totalBytes: state.totalSize,
        percentage: state.status === 'completed' ? 100 : (uploadedParts / totalParts) * 100,
        status: state.status
      };
    });

    this.onProgress(progress);
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.session.sessionId;
  }
}

/**
 * Create a new upload session
 */
export function createUploadSession(
  userId: string,
  token: string,
  metadata: SessionMetadata,
  onProgress?: (progress: UploadProgress[]) => void
): StudioUploader {
  const sessionId = uuidv4();
  return new StudioUploader(sessionId, userId, token, metadata, onProgress);
}
